import { api } from '@/lib/api';

type Envelope<T> = { success?: boolean; data?: T; error?: string };

async function unwrap<T>(promise: Promise<Envelope<T>>): Promise<T> {
  const res = await promise;
  if (!res.success || res.data === undefined) throw new Error(res.error || 'Anfrage fehlgeschlagen');
  return res.data;
}

export interface InboxItem {
  id: string;
  platform: string;
  kind: 'comment' | 'mention' | 'reply';
  author_name?: string | null;
  body: string;
  permalink?: string | null;
  occurred_at?: string | null;
  is_read: boolean;
  replied_at?: string | null;
  post_id?: string | null;
  social_posts?: { id: string; title?: string | null; body?: string | null } | null;
  social_accounts?: { display_name?: string | null } | null;
}

export interface ApprovalQueueItem {
  id: string;
  kind: 'social_post' | 'campaign_content';
  entity_id: string;
  campaign_id?: string;
  title: string;
  subtitle?: string;
  href: string;
  submitted_at?: string | null;
}

export interface PublicApprovalPreview {
  kind: 'social_post' | 'campaign_content';
  revision: string;
  title: string;
  body?: string | null;
  content_md?: string | null;
  content_html?: string | null;
  link_url?: string | null;
  first_comment?: string | null;
  planned_at?: string | null;
  platforms?: string[];
  campaign_name?: string | null;
  expires_at: string;
  targets?: Array<{
    id: string; platform: string; account_name: string; title: string; body: string;
    post_kind: string; scheduled_at: string | null; status: string;
    privacy_status?: string | null; category_id?: string | null; notify_subscribers?: boolean;
    options: Record<string, unknown>;
  }>;
  media?: Array<{ id: string; url: string | null; mime_type: string; filename: string; role: string }>;
  newsletter?: { subject: string; list_id: string | null; from_name: string; reply_to: string | null } | null;
}

export class PublicApprovalError extends Error {
  constructor(message: string, public status: number, public decisionSaved = false) { super(message); }
}

async function publicApprovalRequest<T>(token: string, decision?: { action: 'approve' | 'reject'; revision: string }): Promise<T> {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
  const response = await fetch(`${base}/public/approvals/${encodeURIComponent(token)}${decision ? '/decide' : ''}`, {
    method: decision ? 'POST' : 'GET', cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer',
    ...(decision ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(decision) } : {}),
  });
  const result = await response.json() as Envelope<T> & { data?: T & { decision_saved?: boolean } };
  if (!response.ok || !result.success || !result.data) {
    throw new PublicApprovalError(result.error || 'Freigabe konnte nicht geladen werden.', response.status, result.data?.decision_saved === true);
  }
  return result.data;
}

export const publicApprovalApi = {
  preview: (token: string) => publicApprovalRequest<PublicApprovalPreview>(token),
  decide: (token: string, action: 'approve' | 'reject', revision: string) =>
    publicApprovalRequest<{ action: 'approve' | 'reject'; decision_saved: boolean }>(token, { action, revision }),
};

export const engagementApi = {
  inbox: (unread = false) =>
    unwrap(api.get<Envelope<InboxItem[]>>(`/inbox${unread ? '?unread=1' : ''}`)),
  inboxUnread: () => unwrap(api.get<Envelope<{ count: number }>>('/inbox/unread-count')),
  syncInbox: () => unwrap(api.post<Envelope<{ upserted: number; failed: number }>>('/inbox/sync')),
  markInboxRead: (id: string) => api.post<Envelope<unknown>>(`/inbox/${id}/read`),
  replyInbox: (id: string, body: string) =>
    unwrap(api.post<Envelope<{ external_id: string }>>(`/inbox/${id}/reply`, { body })),

  approvalQueue: () => unwrap(api.get<Envelope<ApprovalQueueItem[]>>('/approvals/queue')),
  approvalCount: () => unwrap(api.get<Envelope<{ count: number }>>('/approvals/count')),
  shareApproval: (payload: {
    kind: 'social_post' | 'campaign_content';
    entity_id: string;
    campaign_id?: string;
  }) => unwrap(api.post<Envelope<{ token: string; expires_at: string; path: string }>>('/approvals/share', payload)),
};
